import {BillingAddress, IOrderModel, Item, PickupAddress, ShippingAddress, StorePickup} from './app.types';
import {IOrderForTYP, OrdersApi} from '@wix/wixstores-client-storefront-sdk/dist/es/src/apis/OrdersApi/OrdersApi';
import {
  SubscriptionFrequency,
  GetOrderForTypQuery,
} from '@wix/wixstores-client-storefront-sdk/dist/es/src/graphql/queries-schema';
import {ILocaleDisplayNames} from '@wix/wixstores-client-storefront-sdk/dist/es/src/apis/LocaleApi/LocaleApi';
import {AddressTranslationService} from '../domain/services/AddressTranslationService';
import {prepareSubdivision} from '../components/thankYouPage/order.helper';
import {AdditionalFee} from '@wix/wixstores-graphql-schema-node';

type OrderModelArgs = {
  orderId: string;
  ordersApi: OrdersApi;
  addressTranslationService: AddressTranslationService;
};

export class OrderModel implements IOrderModel {
  private readonly order: IOrderForTYP;
  private readonly shippingTranslations: ILocaleDisplayNames;
  private readonly billingTranslations: ILocaleDisplayNames;
  private readonly storePickupTranslations: ILocaleDisplayNames;

  public static async createModel({
    orderId,
    ordersApi,
    addressTranslationService,
  }: OrderModelArgs): Promise<OrderModel> {
    const gqlOrder = await ordersApi.fetchOrderForTYP({orderId});
    if (gqlOrder.shippingInfo.address.country) {
      const shippingTranslations = await addressTranslationService.translate(
        gqlOrder.shippingInfo.address.country,
        prepareSubdivision(gqlOrder.shippingInfo.address.subdivision)
      );
      let billingTranslations;
      if (gqlOrder.billingInfo?.address?.country) {
        billingTranslations = await addressTranslationService.translate(
          gqlOrder.billingInfo.address.country,
          prepareSubdivision(gqlOrder.billingInfo.address.subdivision)
        );
      }
      return new OrderModel({gqlOrder, shippingTranslations, billingTranslations});
    }

    if (gqlOrder.deliveryInfo?.pickupInfo?.address) {
      const storePickupTranslations = await addressTranslationService.translate(
        gqlOrder.deliveryInfo.pickupInfo.address.country,
        prepareSubdivision(gqlOrder.deliveryInfo.pickupInfo.address.subdivision)
      );
      return new OrderModel({gqlOrder, storePickupTranslations});
    }

    return new OrderModel({gqlOrder});
  }

  private constructor({
    gqlOrder,
    shippingTranslations,
    storePickupTranslations,
    billingTranslations,
  }: {
    gqlOrder: IOrderForTYP;
    shippingTranslations?: ILocaleDisplayNames;
    storePickupTranslations?: ILocaleDisplayNames;
    billingTranslations?: ILocaleDisplayNames;
  }) {
    this.order = gqlOrder;
    this.shippingTranslations = shippingTranslations;
    this.billingTranslations = billingTranslations;
    this.storePickupTranslations = storePickupTranslations;
  }

  public get buyerName(): string {
    return this.order.userInfo.name;
  }

  public get buyerMail(): string {
    return this.order.userInfo.email;
  }

  public get buyerNote(): string | null {
    return this.order.buyerNote;
  }

  public get buyerId(): string {
    return this.order.contactId;
  }

  public get orderId(): number {
    return this.order.incrementId;
  }

  public get totalPrice(): number {
    return this.order.totals.total;
  }

  public get formattedTotalPrice(): string {
    return this.order.formattedTotals.formattedTotal;
  }

  public get formattedAdditionalFeesPrice(): string {
    return this.order?.formattedTotals?.formattedAdditionalFeesPrice ?? '';
  }

  public get additionalFeesPrice(): number {
    return this.order?.totals?.additionalFeesPrice ?? 0;
  }

  public get formattedTaxPrice(): string {
    return this.order.formattedTotals.formattedTax;
  }

  public get formattedShippingPrice(): string {
    return this.order.formattedTotals.formattedShipping;
  }

  public get formattedSubTotalPrice(): string {
    return this.order.formattedTotals.formattedSubTotal;
  }

  public get formattedDiscount(): string {
    return this.order.formattedTotals.formattedDiscount;
  }

  public get formattedTotalGiftCardAmount(): string {
    return this.order.formattedTotals.formattedTotalGiftCardAmount;
  }

  public get hasGiftCard(): boolean {
    return this.order.formattedTotals.hasGiftCard;
  }

  public get totalGiftCardAmount(): number {
    return this.order.totals.totalGiftCardAmount;
  }

  public get formattedTotalPaid(): string {
    return this.order.formattedTotals.formattedTotalPaid;
  }

  public get formattedTotalBalance(): string {
    return this.order.formattedTotals.formattedTotalBalance;
  }

  public get shippingPrice(): number {
    return this.order.totals.shipping;
  }

  public get taxPrice(): number {
    return this.order.totals.tax;
  }

  public get paymentMethod(): string {
    return this.order.billingInfo.method;
  }

  public get payments(): GetOrderForTypQuery['order']['transactions']['payments'] {
    return this.order.transactions?.payments;
  }

  public get paymentStatus(): string {
    return this.order.billingInfo.status;
  }

  public get billingAddress(): BillingAddress {
    const {countryName, regionName} = this.billingTranslations || {};
    return {
      ...this.order.billingInfo.address,
      country: countryName,
      subdivision: regionName || '',
    };
  }

  public get shippingAddress(): ShippingAddress {
    const {countryName, regionName} = this.shippingTranslations || {};
    const {
      company,
      city,
      addressLine,
      addressLine2,
      zipCode,
      phoneNumber: phone,
      fullName,
    } = this.order.shippingInfo.address;

    return {
      company,
      country: countryName,
      subdivision: regionName || '',
      city,
      addressLine,
      addressLine2,
      zipCode,
      phone,
      fullName,
    };
  }

  public get deliveryType(): string {
    return this.order.deliveryInfo?.deliveryType;
  }

  public get storePickup(): StorePickup {
    const title = this.order.deliveryInfo?.title;
    const time = this.order.deliveryInfo?.deliveryTime?.trim();
    const instructions = this.order.deliveryInfo?.pickupInfo?.instructions;
    const address = this.storePickupAddress;
    return {title, time, instructions, address};
  }

  public get couponCode(): string {
    return this.order.totals.discount.code;
  }

  public get items(): Item[] {
    return this.order.items;
  }

  public get additionalFees(): Partial<AdditionalFee>[] {
    return this.order?.additionalFees ?? [];
  }

  public get createdDate(): number {
    return this.order.createdDate;
  }

  public get cartId(): string {
    return this.order.cartId;
  }

  public get checkoutId(): string {
    return this.order.checkoutId;
  }

  public get isValid(): boolean {
    return this.orderId !== 0;
  }

  public get subscriptionFrequency(): SubscriptionFrequency {
    return null;
  }

  public get subscriptionDuration(): number {
    return 0;
  }

  public get subscriptionName(): string {
    return '';
  }

  public get subscriptionInterval(): number {
    return 0;
  }

  public hasShippingCountryNameTranslation(): boolean {
    return !!this.shippingTranslations?.countryName;
  }

  private get storePickupAddress(): PickupAddress {
    if (!this.order.deliveryInfo?.pickupInfo?.address) {
      return null;
    }

    const {countryName, regionName} = this.storePickupTranslations;

    const {addressLine, city, zipCode} = this.order.deliveryInfo.pickupInfo.address;
    return {
      addressLine,
      city,
      zipCode,
      country: countryName,
      state: regionName || '',
    };
  }
}
